// ─── seo/siteMeta.js ─────────────────────────────────────────────────────────
// One place for every static page's search metadata, so titles and descriptions
// can be reviewed as a set instead of hunted down across twenty page files.
//
// Positioning these are written around: UniFreelancer is a freelance
// marketplace for verified university students and alumni.
//
// Do not mention the fee at all here or in page copy — ours isn't
// meaningfully cheaper than Upwork's combined take, so leading with a number
// just invites a price comparison we don't win. Fee details belong on pages
// someone reaches after they've already decided to sign up (the signup flow,
// ToS), not the first line a stranger reads on Google.
//
// Every phrase carries the university qualifier — that niche, not price, is
// what we can realistically rank for against Fiverr and Upwork.

export const SITE_NAME = 'UniFreelancer';
export const SITE_URL = 'https://www.unifreelancer.com';

// Shipped in /public so prerendered pages and link unfurls both resolve it.
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.png`;

export const DEFAULT_DESCRIPTION =
  'The freelance marketplace built for university students and alumni. Hire verified ' +
  'student talent for your project, or list your own skills.';

// Titles stay under ~60 characters so Google shows them untruncated; the
// `| UniFreelancer` suffix is appended by the Seo component, not repeated here.
export const PAGE_META = {
  home: {
    title: 'Hire Verified University Student Freelancers',
    description: DEFAULT_DESCRIPTION,
    path: '/',
  },
  help: {
    title: 'Help & Support',
    description: 'Clear, step by step guides for buying, selling, getting paid, and everything else on UniFreelancer.',
    path: '/help',
  },
  about: {
    title: 'About Us: Freelancing Built for Students',
    description:
      'How UniFreelancer connects verified university students and alumni with clients, ' +
      'and teaches the skills to freelance successfully.',
    path: '/about',
  },
  browseFreelancers: {
    title: 'Browse Student Freelancers',
    description:
      'Find and hire university students and alumni for design, development, writing, video, ' +
      'tutoring, and more. Every profile is a verified university affiliate.',
    path: '/browse-freelancers',
  },
  hireTalent: {
    title: 'Hire Student Talent for Your Project',
    description:
      'Post a project and hire verified university students fast. Vetted freelancers, ' +
      'secure Stripe payments, and service agreements built in.',
    path: '/hire-talent',
  },
  findWork: {
    title: 'Post a Service: Turn Your Skills Into Income',
    description:
      'Create a listing, set your rate, and start connecting with clients who value your ' +
      'university affiliation.',
    path: '/find-work',
  },
  jobPostings: {
    title: 'Browse Freelance Jobs',
    description:
      'Real projects from businesses and individuals looking for student talent. Find work ' +
      'that fits your skills and apply directly.',
    path: '/job-postings',
  },
  postAJob: {
    title: 'Post a Job: Hire University Students',
    description:
      'Describe your project and reach verified university student freelancers. ' +
      'Free to post, pay only when you hire.',
    path: '/post-a-job',
  },
  academy: {
    title: 'UniFreelancer Academy: Learn to Freelance',
    description:
      'Courses, seminars, and tutorials taught by working professionals. Build real skills and ' +
      'a portfolio, then land your first client.',
    path: '/academy',
  },
  courses: {
    title: 'Online Courses for Student Freelancers',
    description:
      'Learn design, development, writing, and freelance business skills with courses built ' +
      'for university students starting out.',
    path: '/academy/courses',
  },
  seminars: {
    title: 'Live Seminars for Student Freelancers',
    description:
      'Join live seminars with working professionals. Learn freelancing skills in real time ' +
      'on UniFreelancer Academy.',
    path: '/academy/seminars',
  },
  tutorials: {
    title: 'Freelancing Tutorials for Students',
    description:
      'Free and premium tutorials on freelancing, client work, pricing, and the in-demand ' +
      'skills students can sell today.',
    path: '/academy/tutorials',
  },
  podcasts: {
    title: 'Freelancing Podcasts for Students',
    description:
      'Listen to conversations with working freelancers about pricing, clients, and building ' +
      'a freelance career while still in school.',
    path: '/academy/podcasts',
  },
  projectStudio: {
    title: 'Project Studio: Ready-to-Teach Freelance Programs for Universities',
    description:
      'A ready-made freelancing program you teach inside your own course. Students find what they can ' +
      'charge for, learn to handle a client, and finish with a portfolio.',
    path: '/project-studio',
  },
  projectStudioApply: {
    title: 'Bring Project Studio to Your University',
    description:
      'Apply for Faculty verification to run UniFreelancer Project Studio for your students.',
    path: '/project-studio/apply',
    noindex: true,
  },
  social: {
    title: 'The Student Freelancer Community',
    description:
      'Swap advice, share wins, and find collaborators with other university student ' +
      'freelancers on the UniFreelancer community board.',
    path: '/social',
  },
  privacy: {
    title: 'Privacy Policy',
    description: 'How UniFreelancer collects, uses, and protects your data.',
    path: '/privacy',
  },
  terms: {
    title: 'Terms of Service',
    description:
      'The terms governing use of the UniFreelancer platform, including our 15% total ' +
      'service fee and payment protection.',
    path: '/terms',
  },
  paymentsAndTaxes: {
    title: 'How Payments & Taxes Work',
    description:
      'How escrow, fees, and freelancer tax reporting work on UniFreelancer, in plain terms.',
    path: '/payments-and-taxes',
  },
  login: { title: 'Log In', description: 'Log in to your UniFreelancer account.', path: '/login' },
  signup: {
    title: 'Sign Up: Start Freelancing at University',
    description:
      'Create a free UniFreelancer account. Sell your skills as a student or alumnus, ' +
      'or hire verified university talent.',
    path: '/signup',
  },
};

// Routes that are prerendered at build time (see scripts/prerender.js) and
// listed in the sitemap. Only pages that are meaningful to a logged-out
// visitor belong here — everything else is account UI.
export const PUBLIC_ROUTES = Object.values(PAGE_META)
  .filter((m) => !m.noindex)
  .map((m) => m.path);

// Prefixes that must never be indexed: account, payment, and staff surfaces.
// Mirrored by public/robots.txt — keep the two in step.
export const PRIVATE_PATH_PREFIXES = [
  '/reset-password',
  '/profile',
  '/settings',
  '/inbox',
  '/orders',
  '/payment',
  '/admin',
  '/my-',
  '/hiring/',
  '/seller/',
  '/instructor/',
  '/academy/create',
  '/academy/my-',
  '/academy/payment-success',
  '/academy/submissions',
  '/project-studio/browse',
  '/project-studio/apply',
  '/find-work/create-listing',
  '/find-work/edit-listing',
  '/job-postings/create',
  '/job-postings/edit',
  '/work',
  '/calendar',
  '/notifications',
  '/saved',
  '/mentor/',
  '/professor',
  '/classes/',
  '/join',
  '/teams/new',
  '/login',
  '/forgot-password',
  '/get-started',
  '/username',
];
